import { c as _c } from "react/compiler-runtime";
import { Button, Checkbox, DropdownMenu, Input, InputDecorator, Skeleton } from '@common/components';
import { SvgChevrondown, SvgRefresh } from '@common/icon';
import { cn } from '@core/utils';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';
import { TERM_CATEGORY, UnauthQueryOptions } from '@/api';
import { BREAKPOINTS } from '@/constants/breakpoints';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useSubscribedState } from '@/hooks/useSubscribedState';
import { useGlobalStore } from '@/stores/GlobalStore';
import { DEFAULT_DONATION_AMOUNT, MIN_DONATION_AMOUNT } from './constants';
interface PrefillFormProps {
  institutionId: number;
  defaultDonationBusinessId?: number;
  onSubmit: (data: {
    amount: number;
    purpose: string;
  }) => void;
}
export const PrefillForm = t0 => {
  const $ = _c(149);
  const {
    institutionId,
    defaultDonationBusinessId,
    onSubmit
  } = t0;
  const isDesktopViewport = useMediaQuery(BREAKPOINTS.DESKTOP, true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      welloTerms: false,
      privacyCollection: false,
      thirdPartyInfo: false,
      membershipTerms: false,
      addressVerification: false,
      shippingInfo: false,
      voluntaryDonation: false,
      refundPolicy: false,
      giftInfo: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [agreements, setAgreements] = useState(t1);
  const [isThirdPartyExpanded, setIsThirdPartyExpanded] = useState(false);
  const setTermsDrawerType = useGlobalStore(_temp);
  let t2;
  if ($[1] !== setTermsDrawerType) {
    t2 = termType => {
      setTermsDrawerType(termType);
    };
    $[1] = setTermsDrawerType;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const handleTermsView = t2;
  const requiredAgreements = ["welloTerms", "privacyCollection", "thirdPartyInfo", "membershipTerms", "addressVerification", "shippingInfo", "voluntaryDonation", "refundPolicy", "giftInfo"] as const;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["membershipTerms", "addressVerification", "shippingInfo"];
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const thirdPartySubItems = t3 as const;
  const isAgreementAll = requiredAgreements.every(key => agreements[key]);
  const isThirdPartyAllChecked = thirdPartySubItems.every(key_0 => agreements[key_0]);
  let t4;
  if ($[4] !== isAgreementAll) {
    t4 = () => {
      const newValue = !isAgreementAll;
      setAgreements({
        welloTerms: newValue,
        privacyCollection: newValue,
        thirdPartyInfo: newValue,
        membershipTerms: newValue,
        addressVerification: newValue,
        shippingInfo: newValue,
        voluntaryDonation: newValue,
        refundPolicy: newValue,
        giftInfo: newValue
      });
    };
    $[4] = isAgreementAll;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const toggleAllAgreements = t4;
  let t5;
  if ($[6] !== agreements) {
    t5 = (key_1, checked) => {
      const newAgreements = {
        ...agreements,
        [key_1]: checked
      };
      if (key_1 === "thirdPartyInfo") {
        thirdPartySubItems.forEach(subKey => {
          newAgreements[subKey] = checked;
        });
      }
      if (thirdPartySubItems.includes(key_1 as (typeof thirdPartySubItems)[number])) {
        const allSubItemsChecked = thirdPartySubItems.every(subKey_0 => subKey_0 === key_1 ? checked : newAgreements[subKey_0]);
        newAgreements.thirdPartyInfo = allSubItemsChecked;
      }
      setAgreements(newAgreements);
    };
    $[6] = agreements;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const updateAgreement = t5;
  let t6;
  if ($[8] !== isThirdPartyExpanded) {
    t6 = () => {
      setIsThirdPartyExpanded(!isThirdPartyExpanded);
    };
    $[8] = isThirdPartyExpanded;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const toggleThirdPartyAccordion = t6;
  const [amount, setAmount] = useState(DEFAULT_DONATION_AMOUNT);
  let t7;
  if ($[10] !== institutionId) {
    t7 = UnauthQueryOptions["get:/wello/v2/allows/home/donation-business/institution/{institutionId}"]({
      path: {
        institutionId
      }
    });
    $[10] = institutionId;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t7) {
    t8 = {
      ...t7,
      select: _temp2
    };
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const {
    data: donationBusinessPurposes
  } = useQuery(t8);
  let t9;
  if ($[14] !== defaultDonationBusinessId || $[15] !== donationBusinessPurposes) {
    t9 = donationBusinessPurposes && donationBusinessPurposes.length > 0 ? (donationBusinessPurposes.find(businessPurpose => businessPurpose.donationBusinessId === defaultDonationBusinessId) ?? donationBusinessPurposes[0]).donationBusinessName : "";
    $[14] = defaultDonationBusinessId;
    $[15] = donationBusinessPurposes;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const defaultPurpose = t9;
  const [purpose, setPurpose] = useSubscribedState(defaultPurpose);
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = value => {
      const sanitizedValue = value.replaceAll(/[^0-9]/g, "");
      if (!/^[\d,]*$/.test(sanitizedValue)) {
        return;
      }
      setAmount(Number(sanitizedValue));
    };
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const handleAmountChange = t10;
  let t11;
  if ($[18] !== donationBusinessPurposes?.[0]?.donationBusinessName || $[19] !== setPurpose) {
    t11 = () => {
      setAmount(DEFAULT_DONATION_AMOUNT);
      setPurpose(donationBusinessPurposes?.[0]?.donationBusinessName ?? "");
      setAgreements({
        welloTerms: false,
        privacyCollection: false,
        thirdPartyInfo: false,
        membershipTerms: false,
        addressVerification: false,
        shippingInfo: false,
        voluntaryDonation: false,
        refundPolicy: false,
        giftInfo: false
      });
    };
    $[18] = donationBusinessPurposes?.[0]?.donationBusinessName;
    $[19] = setPurpose;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const handleReset = t11;
  const t12 = isDesktopViewport ? "H56" : "H48";
  let t13;
  if ($[21] !== amount) {
    t13 = amount.toLocaleString();
    $[21] = amount;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = e => handleAmountChange(e.target.value);
    t15 = <span className="font-400 text-Gray-900 typo-body3">원</span>;
    $[23] = t14;
    $[24] = t15;
  } else {
    t14 = $[23];
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t12 || $[26] !== t13) {
    t16 = <Input dimensions={t12} placeholder="0" inputMode="numeric" value={t13} onChange={t14} rightContent={t15} maxLength={16} className="mb-24" />;
    $[25] = t12;
    $[26] = t13;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== donationBusinessPurposes || $[29] !== isDesktopViewport || $[30] !== purpose || $[31] !== setPurpose) {
    t17 = <InputDecorator label={"\uAE30\uBD80\uC6A9\uB3C4 \uC120\uD0DD"}>{donationBusinessPurposes ? <DropdownMenu dimensions={isDesktopViewport ? "H56" : "H48"} options={donationBusinessPurposes.map(_temp3)} value={purpose} onChange={setPurpose} placeholder={"\uAE30\uBD80\uC6A9\uB3C4\uB97C \uC120\uD0DD\uD574\uC8FC\uC138\uC694"} className="mb-24 w-full" /> : <Skeleton className="mb-24 h-48 w-full rounded-small" />}</InputDecorator>;
    $[28] = donationBusinessPurposes;
    $[29] = isDesktopViewport;
    $[30] = purpose;
    $[31] = setPurpose;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  let t19;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <span className={cn("flex gap-8 font-500 text-Gray-800 typo-body3", "mobile-tablet:typo-body4")}>기부 금액</span>;
    t19 = cn("font-700 typo-h6", "mobile-tablet:typo-body1");
    $[33] = t18;
    $[34] = t19;
  } else {
    t18 = $[33];
    t19 = $[34];
  }
  let t20;
  if ($[35] !== amount) {
    t20 = amount.toLocaleString("ko-KR");
    $[35] = amount;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  let t21;
  if ($[37] !== t20) {
    t21 = <div className="mb-20 flex items-center justify-between border-y-1 border-Gray-300 py-20">{t18}<span className={t19}>{t20}원</span></div>;
    $[37] = t20;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  let t22;
  if ($[39] !== amount) {
    t22 = amount < MIN_DONATION_AMOUNT && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }} exit={{
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeIn"
      }
    }} className="flex flex-col gap-8"><p className="text-center font-400 text-R-500 typo-body4">{MIN_DONATION_AMOUNT.toLocaleString()}원 이상부터 기부 가능해요.</p></motion.div>;
    $[39] = amount;
    $[40] = t22;
  } else {
    t22 = $[40];
  }
  let t23;
  if ($[41] !== t22) {
    t23 = <AnimatePresence>{t22}</AnimatePresence>;
    $[41] = t22;
    $[42] = t23;
  } else {
    t23 = $[42];
  }
  let t24;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <p className={cn("mb-16 text-start text-Gray-700 typo-body3", "mobile-tablet:mb-16 mobile-tablet:typo-body4")}>이용약관에 동의해주세요.</p>;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== isAgreementAll || $[45] !== toggleAllAgreements) {
    t25 = <div className="mb-16 rounded-xsmall bg-Gray-100 px-12 py-8"><Checkbox className="font-600" checked={isAgreementAll} onCheckedChange={toggleAllAgreements}>전체 동의</Checkbox></div>;
    $[44] = isAgreementAll;
    $[45] = toggleAllAgreements;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== updateAgreement) {
    t26 = checked_0 => updateAgreement("welloTerms", checked_0);
    $[47] = updateAgreement;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <div className="flex items-start gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">웰로 고향사랑기부제 이용약관</span></div>;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== handleTermsView) {
    t28 = <div className="flex flex-1 items-start justify-between gap-4">{t27}<button className="font-600 underline typo-body4" type="button" onClick={e_0 => {
        e_0.stopPropagation();
        handleTermsView(TERM_CATEGORY.GOHYANG_LOVE_DONATION_TERMS);
      }}>보기</button></div>;
    $[50] = handleTermsView;
    $[51] = t28;
  } else {
    t28 = $[51];
  }
  let t29;
  if ($[52] !== agreements.welloTerms || $[53] !== t26 || $[54] !== t28) {
    t29 = <Checkbox checked={agreements.welloTerms} onCheckedChange={t26} className="justify-between">{t28}</Checkbox>;
    $[52] = agreements.welloTerms;
    $[53] = t26;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== updateAgreement) {
    t30 = checked_1 => updateAgreement("privacyCollection", checked_1);
    $[56] = updateAgreement;
    $[57] = t30;
  } else {
    t30 = $[57];
  }
  let t31;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <div className="flex items-start gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">개인정보 수집이용 동의</span></div>;
    $[58] = t31;
  } else {
    t31 = $[58];
  }
  let t32;
  if ($[59] !== handleTermsView) {
    t32 = <div className="flex flex-1 items-start justify-between gap-4">{t31}<button className="font-600 underline typo-body4" type="button" onClick={e_1 => {
        e_1.stopPropagation();
        handleTermsView(TERM_CATEGORY.PERSONAL_INFO_COLLECTION_AGREEMENT);
      }}>보기</button></div>;
    $[59] = handleTermsView;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  let t33;
  if ($[61] !== agreements.privacyCollection || $[62] !== t30 || $[63] !== t32) {
    t33 = <Checkbox checked={agreements.privacyCollection} onCheckedChange={t30} className="justify-between">{t32}</Checkbox>;
    $[61] = agreements.privacyCollection;
    $[62] = t30;
    $[63] = t32;
    $[64] = t33;
  } else {
    t33 = $[64];
  }
  let t34;
  if ($[65] !== updateAgreement) {
    t34 = checked_2 => updateAgreement("thirdPartyInfo", checked_2);
    $[65] = updateAgreement;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <span className="font-500 text-Gray-black typo-body4">(필수)</span>;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  let t36;
  if ($[68] !== toggleThirdPartyAccordion) {
    t36 = e_2 => {
      e_2.preventDefault();
      toggleThirdPartyAccordion();
    };
    $[68] = toggleThirdPartyAccordion;
    $[69] = t36;
  } else {
    t36 = $[69];
  }
  let t37;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <span className="text-start">고향사랑e음 회원가입 및 연계를 위한 개인정보 제3자 제공 동의</span>;
    $[70] = t37;
  } else {
    t37 = $[70];
  }
  let t38;
  if ($[71] !== isThirdPartyExpanded) {
    t38 = cn("size-16 min-h-21 min-w-16 transition-transform duration-200", {
      "rotate-180": isThirdPartyExpanded
    });
    $[71] = isThirdPartyExpanded;
    $[72] = t38;
  } else {
    t38 = $[72];
  }
  let t39;
  if ($[73] !== t38) {
    t39 = <SvgChevrondown className={t38} />;
    $[73] = t38;
    $[74] = t39;
  } else {
    t39 = $[74];
  }
  let t40;
  if ($[75] !== t36 || $[76] !== t39) {
    t40 = <div className="flex flex-1 items-start justify-between gap-4"><div className="flex items-start gap-4">{t35}<span className="flex cursor-pointer items-start gap-4 font-400 text-Gray-800 typo-body4" onClick={t36}>{t37}{t39}</span></div></div>;
    $[75] = t36;
    $[76] = t39;
    $[77] = t40;
  } else {
    t40 = $[77];
  }
  let t41;
  if ($[78] !== isThirdPartyAllChecked || $[79] !== t34 || $[80] !== t40) {
    t41 = <Checkbox checked={isThirdPartyAllChecked} onCheckedChange={t34} className="justify-between">{t40}</Checkbox>;
    $[78] = isThirdPartyAllChecked;
    $[79] = t34;
    $[80] = t40;
    $[81] = t41;
  } else {
    t41 = $[81];
  }
  let t42;
  if ($[82] !== agreements.addressVerification || $[83] !== agreements.membershipTerms || $[84] !== agreements.shippingInfo || $[85] !== handleTermsView || $[86] !== isThirdPartyExpanded || $[87] !== updateAgreement) {
    t42 = isThirdPartyExpanded && <div className="flex flex-col pl-16"><Checkbox checked={agreements.membershipTerms} onCheckedChange={checked_3 => updateAgreement("membershipTerms", checked_3)} className="justify-between"><div className="flex flex-1 items-start justify-between gap-4"><span className="font-400 text-Gray-800 typo-body4">고향사랑e음 회원가입을 위한 개인정보 제공 동의</span><button className="font-600 underline typo-body4" type="button" onClick={e_3 => {
            e_3.stopPropagation();
            handleTermsView(TERM_CATEGORY.GOHYANG_EUM_SIGNUP_THIRD_PARTY_AGREEMENT);
          }}>보기</button></div></Checkbox><Checkbox checked={agreements.addressVerification} onCheckedChange={checked_4 => updateAgreement("addressVerification", checked_4)} className="justify-between"><div className="flex flex-1 items-start justify-between gap-4"><span className="font-400 text-Gray-800 typo-body4">주소지 확인을 위한 주민등록번호 제공 동의</span><button className="font-600 underline typo-body4" type="button" onClick={e_4 => {
            e_4.stopPropagation();
            handleTermsView(TERM_CATEGORY.RESIDENT_REGISTRATION_PROVIDE_AGREEMENT);
          }}>보기</button></div></Checkbox><Checkbox checked={agreements.shippingInfo} onCheckedChange={checked_5 => updateAgreement("shippingInfo", checked_5)} className="justify-between"><div className="flex flex-1 items-start justify-between gap-4"><span className="font-400 text-Gray-800 typo-body4">답례품 배송 관련 개인정보 제3자 제공 동의</span><button className="font-600 underline typo-body4" type="button" onClick={e_5 => {
            e_5.stopPropagation();
            handleTermsView(TERM_CATEGORY.GIFT_DELIVERY_THIRD_PARTY_AGREEMENT);
          }}>보기</button></div></Checkbox></div>;
    $[82] = agreements.addressVerification;
    $[83] = agreements.membershipTerms;
    $[84] = agreements.shippingInfo;
    $[85] = handleTermsView;
    $[86] = isThirdPartyExpanded;
    $[87] = updateAgreement;
    $[88] = t42;
  } else {
    t42 = $[88];
  }
  let t43;
  if ($[89] !== t41 || $[90] !== t42) {
    t43 = <div className="flex flex-col gap-8">{t41}{t42}</div>;
    $[89] = t41;
    $[90] = t42;
    $[91] = t43;
  } else {
    t43 = $[91];
  }
  let t44;
  if ($[92] !== updateAgreement) {
    t44 = checked_6 => updateAgreement("voluntaryDonation", checked_6);
    $[92] = updateAgreement;
    $[93] = t44;
  } else {
    t44 = $[93];
  }
  let t45;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <span className="flex gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">공무 관련 강요 없이 자발적으로 기부합니다.</span></span>;
    $[94] = t45;
  } else {
    t45 = $[94];
  }
  let t46;
  if ($[95] !== handleTermsView) {
    t46 = <div className="flex flex-1 items-start justify-between gap-4">{t45}<button className="font-600 underline typo-body4" type="button" onClick={e_6 => {
        e_6.stopPropagation();
        handleTermsView(TERM_CATEGORY.TERMS_GOVERNMENT_PRESSURE);
      }}>보기</button></div>;
    $[95] = handleTermsView;
    $[96] = t46;
  } else {
    t46 = $[96];
  }
  let t47;
  if ($[97] !== agreements.voluntaryDonation || $[98] !== t44 || $[99] !== t46) {
    t47 = <Checkbox checked={agreements.voluntaryDonation} onCheckedChange={t44} className="justify-between">{t46}</Checkbox>;
    $[97] = agreements.voluntaryDonation;
    $[98] = t44;
    $[99] = t46;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] !== updateAgreement) {
    t48 = checked_7 => updateAgreement("refundPolicy", checked_7);
    $[101] = updateAgreement;
    $[102] = t48;
  } else {
    t48 = $[102];
  }
  let t49;
  if ($[103] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <span className="flex gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">안내사항(환불 불가·세액공제 등)을 확인했습니다.</span></span>;
    $[103] = t49;
  } else {
    t49 = $[103];
  }
  let t50;
  if ($[104] !== handleTermsView) {
    t50 = <div className="flex flex-1 items-start justify-between gap-4">{t49}<button className="font-600 underline typo-body4" type="button" onClick={e_7 => {
        e_7.stopPropagation();
        handleTermsView(TERM_CATEGORY.TERMS_GUIDANCE_NOTICE);
      }}>보기</button></div>;
    $[104] = handleTermsView;
    $[105] = t50;
  } else {
    t50 = $[105];
  }
  let t51;
  if ($[106] !== agreements.refundPolicy || $[107] !== t48 || $[108] !== t50) {
    t51 = <Checkbox checked={agreements.refundPolicy} onCheckedChange={t48} className="justify-between">{t50}</Checkbox>;
    $[106] = agreements.refundPolicy;
    $[107] = t48;
    $[108] = t50;
    $[109] = t51;
  } else {
    t51 = $[109];
  }
  let t52;
  if ($[110] !== updateAgreement) {
    t52 = checked_8 => updateAgreement("giftInfo", checked_8);
    $[110] = updateAgreement;
    $[111] = t52;
  } else {
    t52 = $[111];
  }
  let t53;
  if ($[112] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <span className="flex gap-4"><span className="font-500 text-Gray-black typo-body4">(필수)</span><span className="font-400 text-Gray-800 typo-body4">답례품 정보 확인 및 동의</span></span>;
    $[112] = t53;
  } else {
    t53 = $[112];
  }
  let t54;
  if ($[113] !== handleTermsView) {
    t54 = <div className="flex flex-1 items-start justify-between gap-4">{t53}<button className="font-600 underline typo-body4" type="button" onClick={e_8 => {
        e_8.stopPropagation();
        handleTermsView(TERM_CATEGORY.TERMS_GIFT_INFO_CHECK_AGREEMENT);
      }}>보기</button></div>;
    $[113] = handleTermsView;
    $[114] = t54;
  } else {
    t54 = $[114];
  }
  let t55;
  if ($[115] !== agreements.giftInfo || $[116] !== t52 || $[117] !== t54) {
    t55 = <Checkbox checked={agreements.giftInfo} onCheckedChange={t52} className="justify-between">{t54}</Checkbox>;
    $[115] = agreements.giftInfo;
    $[116] = t52;
    $[117] = t54;
    $[118] = t55;
  } else {
    t55 = $[118];
  }
  let t56;
  if ($[119] !== t29 || $[120] !== t33 || $[121] !== t43 || $[122] !== t47 || $[123] !== t51 || $[124] !== t55) {
    t56 = <div className="mb-32 flex flex-col">{t29}{t33}{t43}{t47}{t51}{t55}</div>;
    $[119] = t29;
    $[120] = t33;
    $[121] = t43;
    $[122] = t47;
    $[123] = t51;
    $[124] = t55;
    $[125] = t56;
  } else {
    t56 = $[125];
  }
  let t57;
  if ($[126] !== t21 || $[127] !== t23 || $[128] !== t25 || $[129] !== t56) {
    t57 = <div className="flex flex-col">{t21}{t23}{t24}{t25}{t56}</div>;
    $[126] = t21;
    $[127] = t23;
    $[128] = t25;
    $[129] = t56;
    $[130] = t57;
  } else {
    t57 = $[130];
  }
  let t58;
  if ($[131] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <SvgRefresh className="size-16" />;
    $[131] = t58;
  } else {
    t58 = $[131];
  }
  let t59;
  if ($[132] !== handleReset) {
    t59 = <Button theme="sub-line" onClick={handleReset}>{t58}초기화</Button>;
    $[132] = handleReset;
    $[133] = t59;
  } else {
    t59 = $[133];
  }
  const t60 = !isAgreementAll || !amount || !purpose || amount < MIN_DONATION_AMOUNT;
  let t61;
  if ($[134] !== amount || $[135] !== onSubmit || $[136] !== purpose) {
    t61 = () => onSubmit({
      amount,
      purpose
    });
    $[134] = amount;
    $[135] = onSubmit;
    $[136] = purpose;
    $[137] = t61;
  } else {
    t61 = $[137];
  }
  let t62;
  if ($[138] !== t60 || $[139] !== t61) {
    t62 = <Button disabled={t60} onClick={t61} className="grow">기부하기</Button>;
    $[138] = t60;
    $[139] = t61;
    $[140] = t62;
  } else {
    t62 = $[140];
  }
  let t63;
  if ($[141] !== t59 || $[142] !== t62) {
    t63 = <footer className="flex gap-8">{t59}{t62}</footer>;
    $[141] = t59;
    $[142] = t62;
    $[143] = t63;
  } else {
    t63 = $[143];
  }
  let t64;
  if ($[144] !== t16 || $[145] !== t17 || $[146] !== t57 || $[147] !== t63) {
    t64 = <div>{t16}{t17}{t57}{t63}</div>;
    $[144] = t16;
    $[145] = t17;
    $[146] = t57;
    $[147] = t63;
    $[148] = t64;
  } else {
    t64 = $[148];
  }
  return t64;
};
function _temp(state) {
  return state.setTermsDrawerType;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return context?.contents;
}
function _temp3(t0) {
  const {
    donationBusinessName: donation_business_name
  } = t0;
  return {
    label: donation_business_name,
    value: donation_business_name
  };
}